import { IconPlus } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import {
	useDeleteTasks,
	useTasksPaged,
} from "@/common/artemis-operations/tasks";
import {
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import { useNotify } from "@/common/use-notify";
import { BasePageContainer, CustomIcon, QueryGuard } from "@/components/base";
import { TaskTable } from "@/components/tasks/task-table";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/tasks/",
)({
	component: Tasks,
	// Pre-selects the status filter, e.g. the dashboard's "Failed tasks" card.
	validateSearch: (s: Record<string, unknown>): { status?: string } => ({
		status: typeof s.status === "string" ? s.status : undefined,
	}),
});

function Tasks() {
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const { openTaskCreateWizard } = useWizards();
	const { mutate: deleteTasks } = useDeleteTasks();
	const notify = useNotify();
	const queryClient = useQueryClient();

	const { workspaceId } = Route.useParams();
	const { status: initialStatus } = Route.useSearch();
	const list = useTasksPaged(workspaceId, initialStatus);

	useEffect(() => {
		setTitle("Tasks");
		setActions([
			{
				type: "button",
				id: "create-task",
				tooltip: "New Task",
				icon: <CustomIcon icon={IconPlus} />,
				onClick: () => openTaskCreateWizard(),
			},
		]);
	}, [setTitle, openTaskCreateWizard, setActions]);

	return (
		<QueryGuard isPending={list.loading} isError={list.isError}>
			<BasePageContainer>
				<TaskTable
					data={list.rows}
					loading={list.isFetching}
					totalCount={list.totalCount}
					hasNextPage={list.hasNextPage}
					hasPreviousPage={list.hasPreviousPage}
					pageIndex={list.pageIndex}
					onNextPage={list.onNextPage}
					onPreviousPage={list.onPreviousPage}
					pageSize={50}
					manualSorting
					initialSorting={list.sort}
					onSortingChange={list.setSort}
					search={list.search}
					onSearchChange={list.setSearch}
					status={list.filters.status}
					onStatusChange={(v) => list.setFilter("status", v)}
					onRefresh={() => list.refetch()}
					onDeleteTasks={(tasks) => {
						deleteTasks(
							tasks.map((t) => t.id),
							{
								onSuccess: () => {
									notify.success(DELETE_RESOURCE_INFO_COMPLETED);
									queryClient.invalidateQueries({
										queryKey: ["tasks", workspaceId],
									});
								},
								onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
							},
						);
					}}
				/>
			</BasePageContainer>
		</QueryGuard>
	);
}
